"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_UA_KEY_EVENTS_NAME } from "./constants";
import type { UpdateUserAnalyticsKeyEventsInput } from "./schema";

const PreviewQuery = graphql(`
  query UaKeyEventsConfirmPreview(
    $projectId: ID!
    $userAnalyticsId: ID!
    $useProject: Boolean!
    $useUa: Boolean!
  ) {
    project: node(id: $projectId) @include(if: $useProject) {
      __typename
      ... on UserAnalyticsProject {
        name
        userAnalytics {
          id
          keyEvents
        }
      }
    }
    ua: node(id: $userAnalyticsId) @include(if: $useUa) {
      __typename
      ... on UserAnalytics {
        id
        keyEvents
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateUserAnalyticsKeyEventsConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget =
    isToolUIPart(part) && getToolName(part) === UPDATE_UA_KEY_EVENTS_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ??
    {}) as Partial<UpdateUserAnalyticsKeyEventsInput>;
  const projectId = input.projectId ?? "";
  const userAnalyticsId = input.userAnalyticsId ?? "";

  const useProject = Boolean(projectId);
  const useUa = Boolean(userAnalyticsId) && !useProject;

  const [{ data, fetching }] = useQuery({
    query: PreviewQuery,
    variables: {
      projectId,
      userAnalyticsId,
      useProject,
      useUa,
    },
    pause: !(isTarget && isInputAvailable && (useProject || useUa)),
  });

  const projectName =
    data?.project?.__typename === "UserAnalyticsProject"
      ? data.project.name
      : undefined;

  const previousKeyEvents =
    data?.project?.__typename === "UserAnalyticsProject"
      ? (data.project.userAnalytics?.keyEvents ?? [])
      : data?.ua?.__typename === "UserAnalytics"
        ? (data.ua.keyEvents ?? [])
        : undefined;

  if (!isTarget || !isInputAvailable) return null;

  const nextEvents = input.keyEvents ?? [];

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Update User Analytics key events?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>New key events</Trans>:{" "}
          <span className="font-mono text-xs">
            {nextEvents.length > 0 ? nextEvents.join(", ") : "—"}
          </span>
        </li>
        {/* Gate on synchronously-known value so Skeleton is reachable during fetch */}
        {useProject || useUa ? (
          <li>
            <Trans>Current key events</Trans>:{" "}
            {fetching ? (
              <Skeleton className="inline-block h-4 w-24" />
            ) : (
              <span className="font-mono text-xs">
                {(previousKeyEvents ?? []).length > 0
                  ? (previousKeyEvents ?? []).join(", ")
                  : "—"}
              </span>
            )}
          </li>
        ) : null}
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName ?? "Unknown"}</span>
            )}
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
